'use client';

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { showToast } from '../../../utils/toast';
import { trackedMarkupError } from '../../../utils/listingPricing';
import { API } from '../../../api/config/axiosConfig';
import addressModule from '../../../api/modules/address.module';
import CardDetailsStep from './components/CardDetailsStep/CardDetailsStep';
import UploadImagesStep from './components/UploadImagesStep/UploadImagesStep';
import PricingQuantityStep from './components/PricingQuantityStep/PricingQuantityStep';
import ReviewPublishStep from './components/ReviewPublishStep/ReviewPublishStep';
import CreateListingView from './create-listing.view';
import { preferredListingAddress } from './listingAddress';

import { useListing } from '../../../Contexts/ListingProvider';
import { track } from '../../../analytics/track';
import { EVENTS } from '../../../analytics/events';

/**
 * /create-listing, the container.
 *
 * Owns the step, the form data, the validation, the one POST, the analytics
 * event and the redirect. Everything visual lives in `create-listing.view.jsx`
 * and in the four step views, all of which are pure, so the state explorer can
 * mount every state of this wizard without the API.
 *
 * THE COLLECTION-AND-HANDLING STEP IS GONE, AND ITS TWO FIELDS ARE NOT
 * ---------------------------------------------------------------------
 * The step asked for `address` and `processing_days`. Both are still on the
 * multipart body, byte for byte, because the API takes both. What changed is
 * where they come from: they are facts about the SELLER, the same on every
 * listing that seller will ever make, so re-asking once per listing was the
 * bug. `address` is resolved from the seller's default saved address on the
 * same `['userAddresses']` query key `SavedAddressSelector` uses, so the two
 * share one cache and one source of truth. `processing_days` keeps the
 * wizard's own default of 3 until the dashboard carries an editable one.
 *
 * NOTHING IS SILENTLY DROPPED. If the seller has no saved address there is no
 * address to send, and rather than POST a blank one and take an opaque 400
 * back, `handleSubmit` stops and says where to set it. See `blocked` in the
 * handover: the dashboard quick link is the other half of this and is not in
 * this directory.
 *
 * EVERYTHING ELSE IS UNCHANGED. The step 1 to 3 `validateStep` rules run in
 * the same order with the same `showToast` strings, the multipart body carries
 * the same field names in the same sequence to the same endpoint, and the
 * `EVENTS.LISTING_CREATED` payload and the `navigate('/dashboard')` are the
 * ones that were here. The `errors` object is derived from the SAME
 * validation, so a failed step also marks the field that failed instead of
 * only firing a toast that is gone in four seconds.
 *
 * The `SellerDashboard/Layout` wrapper went: `MarketShell` is the real chrome.
 * Layout carried dead Tailwind classes (`flex flex-col h-screen`) for a
 * framework this app does not install.
 */

const CreateListing = () => {
  const navigate = useNavigate();
  const { lookUpData } = useListing();
  const { user } = useSelector((state) => state.auth);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const titleRef = useRef(null);

  // Form data state
  const [formData, setFormData] = useState({
    card_id: '',
    condition: 'Near Mint',
    finish: 'Normal',
    description: '',
    price: '',
    tcgMarketPrice: null,
    listingPercentage: 10,
    quantity: 1,
    // Filled from the seller's saved address below, not by a wizard step.
    address: '',
    processing_days: 3,
    images: [],
    acceptOffers: false,
    bought_for_price: '',
  });

  /**
   * The seller's collection address.
   *
   * Same query key as `SavedAddressSelector`, so if the seller has already
   * loaded their addresses anywhere in this session this resolves from cache
   * and costs nothing. The default one wins, the first one is the fallback,
   * and the string is assembled exactly the way `SavedAddressSelector`
   * assembles it so the seller reads the same address in both places.
   */
  const { data: savedAddresses } = useQuery({
    queryKey: ['userAddresses'],
    queryFn: async () => {
      const res = await addressModule.getAddresses();
      if (!res.success) return [];
      return res.data || [];
    },
  });

  // No `= []` default on the destructure above: that would mint a new array on
  // every render while the query is in flight and re-run this effect with it.
  useEffect(() => {
    if (formData.address || !savedAddresses?.length) return;

    const preferred = preferredListingAddress(savedAddresses);
    const formatted = [
      preferred.address_line1,
      preferred.address_line2,
      preferred.city,
      preferred.province,
      preferred.postal_code,
    ]
      .filter(Boolean)
      .join(', ');

    if (formatted) {
      setFormData((prev) => ({ ...prev, address: formatted }));
    }
  }, [savedAddresses, formData.address]);

  // Check if user is seller
  useEffect(() => {
    if (user && user.role !== 'seller') {
      showToast.error('Access denied. Only sellers can create listings.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Move focus to the new panel heading after a step change, so a screen reader
  // is told where it landed rather than being left on a button that is gone.
  useEffect(() => {
    titleRef.current?.focus();
  }, [currentStep]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * The original validation, minus the case that no longer has a step.
   *
   * Cases 1 to 3 are untouched: same rules, same order, same `showToast`
   * strings, same field map for the inline errors. Case 4 was the collection
   * step's address / handling-time / `isAddressSelected` gate; there is no
   * step for the seller to fail any more, and the address is resolved rather
   * than typed, so it is checked once at submit instead.
   */
  const validateStep = (step) => {
    // eslint-disable-next-line default-case
    switch (step) {
      case 1:
        if (!formData.card_id || !formData.condition) {
          showToast.error('Please fill in all card details');
          setErrors({
            card_id: !formData.card_id ? 'Pick the catalogue card you are listing' : undefined,
            condition: !formData.condition ? 'Choose a condition' : undefined,
          });
          return false;
        }
        break;
      case 2:
        if (formData.images.length === 0) {
          showToast.error('Please upload at least one image');
          setErrors({ images: 'Add at least one photo of the card' });
          return false;
        }
        if (formData.images.length > 5) {
          showToast.error('Maximum 5 images are allowed');
          setErrors({ images: 'Five photos is the maximum. Remove the extras.' });
          return false;
        }
        break;
      case 3: {
        const quantity = parseFloat(formData.quantity);

        const cleanPrice =
          typeof formData.price === 'string'
            ? parseFloat(formData.price.replace(/,/g, ''))
            : parseFloat(formData.price);

        if (
          !cleanPrice ||
          cleanPrice <= 0 ||
          isNaN(cleanPrice) ||
          !quantity ||
          quantity <= 0 ||
          !Number.isInteger(quantity)
        ) {
          showToast.error('Please enter a valid price and a whole number quantity');
          setErrors({
            price:
              !cleanPrice || cleanPrice <= 0 || isNaN(cleanPrice)
                ? 'Enter a price greater than R0.00'
                : undefined,
            quantity:
              !quantity || quantity <= 0 || !Number.isInteger(quantity)
                ? 'Enter a whole number, at least 1'
                : undefined,
          });
          return false;
        }

        if (cleanPrice < 0.01) {
          showToast.error('Price must be greater than R0.00');
          setErrors({ price: 'Price must be greater than R0.00' });
          return false;
        }

        const markupError = trackedMarkupError({
          mode: formData.pricing_mode,
          markupPercent: formData.markup_percent,
        });
        if (markupError) {
          showToast.error(markupError);
          setErrors({ markup_percent: markupError });
          return false;
        }
        break;
      }
    }
    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      nextStep();
    }
  };

  const handleSubmit = async () => {
    // The last thing the seller filled in is step 3, and Next already gated it,
    // but Publish can be reached by going Back and Forward again, so it is
    // re-checked here — this is the same "validate the final step before
    // posting" the old `validateStep(4)` call was doing.
    if (!validateStep(3)) return;

    // `address` is required by /api/cards/create-card-listing/ and no longer
    // has a step to come from. Refuse here with somewhere to go rather than
    // posting an empty string and letting the server answer with a field name.
    if (!formData.address) {
      const message =
        'Add a collection address to your seller dashboard before publishing a listing.';
      showToast.error(message);
      setSubmitError(message);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Clean price by removing any thousand separators before sending
      const cleanPrice =
        typeof formData.price === 'string'
          ? formData.price.replace(/[,\s]/g, '').trim()
          : formData.price.toString();

      const formDataToSend = new FormData();
      formDataToSend.append('card_id', formData.card_id);
      formDataToSend.append('condition', formData.condition);
      formDataToSend.append('finish', formData.finish);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', cleanPrice);
      formDataToSend.append('pricing_mode', formData.pricing_mode || 'fixed');
      formDataToSend.append('fee_frame', formData.fee_frame || 'inclusive');
      formDataToSend.append(
        'markup_percent',
        formData.markup_percent != null ? formData.markup_percent.toString() : ''
      );
      formDataToSend.append(
        'price_floor',
        formData.price_floor != null ? formData.price_floor.toString() : ''
      );
      formDataToSend.append('tcgMarketPrice', (formData.tcgMarketPrice ?? 0).toString());
      formDataToSend.append('listingPercentage', formData.listingPercentage.toString());
      formDataToSend.append('quantity', formData.quantity.toString());
      formDataToSend.append('address', formData.address);
      formDataToSend.append('processing_days', formData.processing_days.toString());

      // Append bought_for_price if provided (optional field)
      if (formData.bought_for_price && formData.bought_for_price.trim() !== '') {
        formDataToSend.append('bought_for_price', formData.bought_for_price.toString());
      }

      // Append each image file
      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((file) => {
          formDataToSend.append('images', file);
        });
      }

      const response = await API.post('/api/cards/create-card-listing/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        },
      });

      if (response.data.success) {
        track(EVENTS.LISTING_CREATED, {
          listing_id: response.data?.data?.id,
          card_id: formData.card_id,
          price: formData.price,
          quantity: formData.quantity,
          condition: formData.condition,
          tcg_market_price: formData.tcgMarketPrice ?? 0,
          listing_percentage: formData.listingPercentage,
          has_offers: formData.acceptOffers,
          processing_days: formData.processing_days,
        });
        showToast.success(response.data.message || 'Card Listed Successfully');
        navigate('/dashboard');
      } else {
        showToast.error(response.data.message || 'Failed to create listing');
        setSubmitError(response.data.message || 'Failed to create listing');
      }
    } catch (error) {
      console.error('Create listing error:', error);

      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        Object.keys(apiErrors).forEach((key) => {
          showToast.error(`${key}: ${apiErrors[key]}`);
        });
        setSubmitError(
          Object.keys(apiErrors)
            .map((key) => `${key}: ${apiErrors[key]}`)
            .join(' ')
        );
      } else {
        showToast.error(error.response?.data?.message || 'Failed to create listing');
        setSubmitError(error.response?.data?.message || 'Failed to create listing');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepProps = { formData, updateFormData, errors, titleRef };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <CardDetailsStep {...stepProps} />;
      case 2:
        return <UploadImagesStep {...stepProps} />;
      case 3:
        return <PricingQuantityStep {...stepProps} />;
      case 4:
        return (
          <ReviewPublishStep
            {...stepProps}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CreateListingView
      currentStep={currentStep}
      onPrev={prevStep}
      onNext={handleNext}
      stepContent={renderStepContent()}
      card={lookUpData}
      formData={formData}
    />
  );
};

export default CreateListing;
